"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import PlusIcon from "../icons/PlusIcon";
import MinusIcon from "../icons/MinusIcon";
import ExpandableText from "../ui/ExpandableText";
import BuildingIcon from "../icons/BuildingIcon";
import CartIcon from "../icons/CartIcon";
import DeliveryTruckIcon from "../icons/DeliveryTruckIcon";
import MailIcon from "../icons/MailIcon";
import { Button } from "@/components";

type AccordionItem = {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string[];
  link: string;
};

const accordionItems: AccordionItem[] = [
  {
    id: "dispensaries",
    title: "Drop by one of our dispensaries",
    icon: <BuildingIcon size={20} className="w-5 h-5" />,
    content: [
      "Dank dispensaries in Calgary are your number one source for the best cannabis products and prices. For a dank cannabis retail experience, visit us at our Ogden/Lynnwood or Dover cannabis Calgary dispensaries, located conveniently along the Bow River.",
      "Our bright and colourful dispensaries are fully stocked with any cannabis product you need. Shop high-THC, best sellers, locally-produced, best value, and certified dank products. Our danktenders can recommend products and offer expert guidance on safe cannabis use.",
      "Our arcade-style self-service kiosk offers customers a fast, efficient, and user-friendly interactive experience. Quickly and easily browse our entire line of products without the pressure. Dank dispensaries bring the cannabis shopping experience to the 21st century.",
    ],
    link: "/search",
  },
  {
    id: "pickup",
    title: "Order cannabis online, pick up in‑store",
    icon: <CartIcon size={20} className="w-5 h-5" />,
    content: [
      "Skip the wait and order your favorite cannabis products online! Browse our extensive selection of flower, edibles, concentrates, vapes, and more from the comfort of your home. Select your preferred Dank dispensary location for pickup.",
      "Once you place your order, we'll prepare it immediately and send you a text notification when it's ready. Simply visit the dispensary, show your ID, and pick up your order at the counter. It's that easy!",
      "Online ordering saves you time and ensures your products are ready when you arrive. Perfect for busy schedules or when you want to browse without pressure.",
    ],
    link: "/search?fulfillment=pickup",
  },
  {
    id: "same-day",
    title: "Same‑Day weed delivery",
    icon: <DeliveryTruckIcon size={20} className="w-5 h-5" />,
    content: [
      "Need your cannabis products delivered today? Dank offers same-day delivery service throughout Calgary for your convenience. Order before the cutoff time and receive your products the same day.",
      "Our delivery service brings premium cannabis products directly to your door. Choose from our full selection of products, and our trusted delivery team will ensure your order arrives safely and on time.",
      "Same-day delivery is perfect for when you can't make it to the dispensary or prefer the convenience of home delivery. Track your order in real-time and receive updates via text message.",
    ],
    link: "/delivery",
  },
  {
    id: "mail-order",
    title: "Mail order marijuana delivery",
    icon: <MailIcon size={20} className="w-5 h-5" />,
    content: [
      "Can't make it to Calgary? No problem! Dank offers mail order marijuana delivery across Canada. Order your favorite cannabis products online and have them shipped directly to your address.",
      "Our mail order service features discreet packaging and secure shipping to ensure your products arrive safely. Browse our complete catalog and enjoy the same high-quality products available in-store.",
      "Mail order delivery is ideal for customers outside Calgary or those who prefer the convenience of home delivery. Orders are processed quickly and shipped with care, bringing Dank's premium cannabis products to your doorstep.",
    ],
    link: "/delivery",
  },
];

export default function WaysToOrder() {
  const [expandedId, setExpandedId] = useState<string>("dispensaries");

  const toggleAccordion = (id: string) => {
    setExpandedId(expandedId === id ? "" : id);
  };

  const introParagraphs = useMemo(
    () => [
      "Running low on flower, edibles, concentrates, vape oils, or topicals? We've got you. Dank gives Calgary cannabis customers fair prices, quality products, and a quick way to shop. Browse our menu in-store, or order ahead and skip the wait.",
      "Dank dispensaries in Calgary are your number one source for the best cannabis products and prices. For a dank cannabis retail experience, visit us at our Ogden/Lynnwood or Dover cannabis Calgary dispensaries, located conveniently along the Bow River.",
      "Our bright and colourful dispensaries are fully stocked with any cannabis product you need. Shop high-THC, best sellers, locally-produced, best value, and certified dank products. Our danktenders can recommend products and offer expert guidance on safe cannabis use.",
      "Our arcade-style self-service kiosk offers customers a fast, efficient, and user-friendly interactive experience. Quickly and easily browse our entire line of products without the pressure. Dank dispensaries bring the cannabis shopping experience to the 21st century.",
    ],
    []
  );

  const INTRO_COLLAPSED_MAX_HEIGHT_PX = 132;

  return (
    <section
      className="px-4 sm:px-6"
      aria-label="Ways to order from Dank cannabis dispensary"
    >
      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6 lg:gap-12">
          {/* Left Column - Overview */}
          <div className="flex flex-col">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3 leading-tight">
              Ways to order from our Dank cannabis dispensary
            </h2>
            <p className="mb-4 text-body-md text-text-secondary">
              <Link
                href="/how-delivery-works"
                className="font-semibold text-blue-100 underline decoration-blue-100/35 underline-offset-2 transition-colors"
              >
                How delivery works
              </Link>
              <span className="text-text-tertiary">, </span>
              same-day, mail order, and pickup at a glance.
            </p>

            <ExpandableText
              collapsedHeight={INTRO_COLLAPSED_MAX_HEIGHT_PX}
              contentClassName="space-y-4 text-body-md leading-relaxed text-text-secondary lg:text-body-lg"
              fadeClassName="h-20 bg-gradient-to-t from-[#F7F7F8] via-[#F7F7F8]/90 to-transparent"
              buttonClassName="mt-4 inline-flex items-center gap-2 self-start rounded-full border border-blue-10 bg-constant-white px-1 py-1 pl-4 text-body-sm font-medium text-blue-80 transition-colors hover:border-blue-15"
              expandedButtonClassName="border-blue-15 bg-blue-5 text-text-primary"
              iconWrapperClassName="border-blue-10 bg-blue-5/70"
              expandedIconWrapperClassName="border-blue-15 bg-constant-white"
              chevronColor="currentColor"
              expandLabel="Continue reading"
              collapseLabel="Show less"
            >
              {introParagraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </ExpandableText>

            <div className="mt-8 max-sm:hidden">
              <Button asChild variant="primary" size="lg" className="rounded-xl py-3 px-6">
                <Link href="/search">Explore all products</Link>
              </Button>
            </div>
          </div>

          {/* Right Column - Accordion */}
          <div className="flex flex-col gap-4">
            {accordionItems.map((item) => {
              const isExpanded = expandedId === item.id;
              const hasContent = item.content && item.content.length > 0;

              return (
                <div
                  key={item.id}
                  className="rounded-xl shadow-sm border overflow-hidden transition-all duration-300 ease-in-out bg-constant-white border-blue-5"
                >
                  {/* Accordion Header */}
                  <div className="flex w-full items-center justify-between gap-3 bg-constant-white p-4 sm:p-5">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className={`shrink-0 transition-colors duration-200 p-2 rounded-xl bg-blue-5 border border-neutral-10 ${
                          isExpanded
                            ? "text-text-primary"
                            : "text-text-secondary"
                        }`}
                      >
                        {item.icon}
                      </div>
                      <h3
                        id={`ways-to-order-heading-${item.id}`}
                        className={`sm:text-lg text-body-16 font-medium transition-colors duration-200 ${
                          isExpanded
                            ? "text-text-primary"
                            : "text-text-secondary"
                        }`}
                      >
                        {item.title}
                      </h3>
                    </div>
                    <Button
                      type="button"
                      variant="icon"
                      color="white"
                      size="icon-md"
                      aria-expanded={isExpanded}
                      aria-controls={`ways-to-order-panel-${item.id}`}
                      aria-label={
                        isExpanded
                          ? `Collapse: ${item.title}`
                          : `Expand: ${item.title}`
                      }
                      onClick={() => toggleAccordion(item.id)}
                    >
                      {isExpanded ? (
                        <MinusIcon size={16} color="currentColor" />
                      ) : (
                        <PlusIcon size={16} color="currentColor" />
                      )}
                    </Button>
                  </div>

                  {/* Accordion Content */}
                  <div
                    id={`ways-to-order-panel-${item.id}`}
                    role="region"
                    aria-labelledby={`ways-to-order-heading-${item.id}`}
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded
                        ? "max-h-[1000px] opacity-100 p-4"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="space-y-4 bg-constant-white">
                      {hasContent &&
                        item.content.map((paragraph, index) => (
                          <p
                            key={index}
                            className="text-body-md text-text-secondary leading-relaxed"
                          >
                            {paragraph}
                          </p>
                        ))}
                      {/* Shop Now Button - Inside expanded section */}
                      <div className={hasContent ? "pt-4" : ""}>
                        <Button asChild variant="primary" size="lg" className="rounded-xl py-3 px-6">
                          <Link href={item.link}>Shop now</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sm:hidden">
            <Button asChild variant="primary" size="lg" className="rounded-xl py-3 px-6 w-full">
              <Link href="/search">Explore all products</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
